/* eslint-disable no-mixed-spaces-and-tabs */
// TODO - Type 'any' needs to be fixed.
import addDays from "date-fns/addDays";
import startOfWeek from "date-fns/startOfWeek";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import useSchedulerData from "../../../../controllers/use-scheduler-data";
import {
	CopyShiftsDataBySkillsProps,
	CopyShiftsDataProps,
	Job,
	Shift,
	Skill,
	SkillInShift,
	WorkCenter,
} from "../types";
import { getCopyTargetStart, getDayNumber } from "../utils";

interface UseCopyShiftsDialogProps {
	weekPasteDate: { startDate: Date; endDate: Date };
}

const useCopyShiftsDialog = ({ weekPasteDate }: UseCopyShiftsDialogProps) => {
	const {
		filterJobRoles,
		filterAllSkills,
		userData,
		datesOfTheWeek,
		allWeekShifts,
		allWorkcenters,
		shiftsByFilter,
		createDraftShiftAsyncStatus,
		weekStartDay,
	} = useSchedulerData();

	// filterData workcenters are always populated when the scheduler is open
	const filterWorkcenters = useSelector(
		(state: any) => state.supervisor?.filterData?.allWorkcenters
	);
	const filterLocations = useSelector(
		(state: any) => state.supervisor?.filterData?.allLocations
	);

	const [weekCopyDate, setWeekCopyDate] = useState({
		start: addDays(
			startOfWeek(new Date(), { weekStartsOn: getDayNumber(weekStartDay) }),
			1
		),
		end: addDays(
			startOfWeek(new Date(), { weekStartsOn: getDayNumber(weekStartDay) }),
			7
		),
	});
	const [copyToWeekDate, setCopyToWeekDate] = useState({
		start: addDays(
			startOfWeek(new Date(), { weekStartsOn: getDayNumber(weekStartDay) }),
			1
		),
		end: addDays(
			startOfWeek(new Date(), { weekStartsOn: getDayNumber(weekStartDay) }),
			7
		),
	});
	const [checkedUser, setCheckedUser] = useState(true);
	const [checkAll, setCheckAll] = useState(true);
	const [skillCheckAll, setSkillCheckAll] = useState(true);

	//set object for job id to true
	const checkboxes =
		filterJobRoles.length > 0 &&
		filterJobRoles.reduce((acc: any, key: Job) => {
			acc[key.id] = true;
			return acc;
		}, {});

	const skillCheckboxes =
		filterAllSkills.length > 0 &&
		filterAllSkills.reduce((acc: any, key: Skill) => {
			acc[key.id] = true;
			return acc;
		}, {});

	//set object for job id to false

	const checkboxesUnselect =
		filterJobRoles.length > 0 &&
		filterJobRoles.reduce((acc: any, key: Job) => {
			acc[key.id] = false;
			return acc;
		}, {});

	const skillCheckboxesUnselect =
		filterAllSkills.length > 0 &&
		filterAllSkills.reduce((acc: any, key: Job) => {
			acc[key.id] = false;
			return acc;
		}, {});

	const [jobs, setJobs] = useState(checkboxes);
	const [skills, setSkills] = useState(skillCheckboxes);

	const handleClick = ({ id }: { id: number }) => {
		setJobs({ ...jobs, [id]: !jobs[id] });
		setCheckAll(false);
	};

	const handleSkillClick = ({ id }: { id: number }) => {
		setSkills({ ...skills, [id]: !skills[id] });
		setSkillCheckAll(false);
	};

	const handleCheckAll = (e: boolean) => {
		if (e) setJobs(checkboxes);
		else setJobs(checkboxesUnselect);
		setCheckAll((e) => !e);
	};

	const handleSkillCheckAll = (e: boolean) => {
		if (e) setSkills(skillCheckboxes);
		else setSkills(skillCheckboxesUnselect);
		setSkillCheckAll((e) => !e);
	};

	const createFilterCriteria = (state: Record<number, boolean>): number[] =>
		Object.entries(state)
			.filter(([, value]) => value)
			.map(([key]) => Number(key));

	const filterCriteria = useMemo(() => createFilterCriteria(jobs), [jobs]);
	const skillFilterCriteria = useMemo(
		() => createFilterCriteria(skills),
		[skills]
	);

	// Copies that would start before now (e.g. earlier days of the current
	// week) can't be worked, so they are left out of the list
	const isCopyInFuture = useMemo(() => {
		const now = new Date();
		const weekStartsOn = getDayNumber(weekStartDay);
		return (shift: Shift) =>
			getCopyTargetStart(
				shift.startDateTime,
				weekPasteDate.startDate,
				weekStartsOn
			) > now;
	}, [weekPasteDate, weekStartDay]);

	const pastShiftCount = useMemo(
		() =>
			(allWeekShifts ?? []).filter((shift: Shift) => !isCopyInFuture(shift))
				.length,
		[allWeekShifts, isCopyInFuture]
	);

	const copiedShiftData = useMemo(() => {
		if (!weekCopyDate) return [];
		return allWeekShifts
			?.filter((shift: Shift) => filterCriteria.includes(shift.jobId))
			.filter(isCopyInFuture)
			.map((shift: Shift) => {
				const shiftData = filterJobRoles.find(
					(job: Job) => job.id === shift.jobId
				);
				const workcenterData = (filterWorkcenters?.length ? filterWorkcenters : allWorkcenters).find(
					(workcenter: any) => workcenter.id === shift.workCenterId
				);
				return {
					...shift,
					jobName: shiftData?.name,
					workcenter: workcenterData?.name,
					locationId: workcenterData?.locationId,
					locationName: filterLocations?.find(
					(loc: any) => loc.id === workcenterData?.locationId
				)?.name,
					copyDateStart: weekCopyDate.start,
					copyDateEnd: weekCopyDate.end,
					pasteDateStart: weekPasteDate.startDate,
					pasteDateEnd: weekPasteDate.endDate,
				};
			});
	}, [
		weekCopyDate,
		allWeekShifts,
		filterCriteria,
		filterJobRoles,
		allWorkcenters,
		filterWorkcenters,
		filterLocations,
		weekPasteDate,
		isCopyInFuture,
	]);

	const copiedShiftDataBySkills = useMemo(() => {
		if (!weekCopyDate) return [];
		return allWeekShifts
			?.filter((shift: Shift) =>
				shift.skills.some((skill) =>
					skillFilterCriteria.includes(skill.skillId)
				)
			)
			.filter(isCopyInFuture)
			.map((shift: Shift) => {
				const workcenterData = (filterWorkcenters?.length ? filterWorkcenters : allWorkcenters).find(
					(workcenter: any) => workcenter.id === shift.workCenterId
				);
				return {
					...shift,
					shiftSkills: shift.skills.map((skill: SkillInShift) => ({
						...skill,
						id: skill.skillId,
						name: skill.skillName,
					})),
					jobId: null,
					workcenter: workcenterData?.name,
					locationId: workcenterData?.locationId,
					locationName: filterLocations?.find(
					(loc: any) => loc.id === workcenterData?.locationId
				)?.name,
					copyDateStart: weekCopyDate.start,
					copyDateEnd: weekCopyDate.end,
					pasteDateStart: weekPasteDate.startDate,
					pasteDateEnd: weekPasteDate.endDate,
				};
			});
	}, [
		weekCopyDate,
		allWeekShifts,
		skillFilterCriteria,
		allWorkcenters,
		filterWorkcenters,
		filterLocations,
		weekPasteDate,
		isCopyInFuture,
	]);

	const filteredCopiedShiftData = useMemo(
		() =>
			checkedUser
				? copiedShiftData.filter(
						(shift: Shift) => shift.createdBy === parseInt(userData.id ?? "")
				  )
				: copiedShiftData,
		[checkedUser, copiedShiftData, userData.id]
	);

	const filteredCopiedShiftDataBySkills = useMemo(
		() =>
			checkedUser
				? copiedShiftDataBySkills.filter(
						(shift: Shift) => shift.createdBy === parseInt(userData.id ?? "")
				  )
				: copiedShiftDataBySkills,
		[checkedUser, copiedShiftDataBySkills, userData.id]
	);

	const [selectedShiftIds, setSelectedShiftIds] = useState<Set<number>>(
		new Set()
	);

	useEffect(() => {
		const ids = new Set<number>([
			...filteredCopiedShiftData.map((s: Shift) => s.id),
			...filteredCopiedShiftDataBySkills.map((s: Shift) => s.id),
		]);
		setSelectedShiftIds(ids);
	}, [filteredCopiedShiftData, filteredCopiedShiftDataBySkills]);

	const handleShiftToggle = (shiftId: number) => {
		setSelectedShiftIds((prev) => {
			const next = new Set(prev);
			if (next.has(shiftId)) next.delete(shiftId);
			else next.add(shiftId);
			return next;
		});
	};

	const handleShiftSelectAll = (checked: boolean, shiftIds: number[]) => {
		setSelectedShiftIds((prev) => {
			const next = new Set(prev);
			if (checked) shiftIds.forEach((id) => next.add(id));
			else shiftIds.forEach((id) => next.delete(id));
			return next;
		});
	};

	const selectedCopiedShiftData = useMemo(
		() =>
			filteredCopiedShiftData.filter((s: Shift) => selectedShiftIds.has(s.id)),
		[filteredCopiedShiftData, selectedShiftIds]
	);

	const selectedCopiedShiftDataBySkills = useMemo(
		() =>
			filteredCopiedShiftDataBySkills.filter((s: Shift) =>
				selectedShiftIds.has(s.id)
			),
		[filteredCopiedShiftDataBySkills, selectedShiftIds]
	);

	return [
		{
			checkAll,
			skillCheckAll,
			jobs,
			skills,
			checkedUser,
			copiedShiftData: filteredCopiedShiftData,
			copiedShiftDataBySkills: filteredCopiedShiftDataBySkills,
			selectedShiftIds,
			selectedCopiedShiftData,
			selectedCopiedShiftDataBySkills,
			copyToWeekDate,
			datesOfTheWeek,
			weekCopyDate,
			shiftsByFilter,
			createDraftShiftAsyncStatus,
			pastShiftCount,
		},
		{
			setWeekCopyDate,
			setCopyToWeekDate,
			handleCheckAll,
			handleSkillCheckAll,
			handleClick,
			handleSkillClick,
			setCheckedUser,
			handleShiftToggle,
			handleShiftSelectAll,
		},
	];
};

export default useCopyShiftsDialog;
